import { Button, Spinner } from "@shopify/polaris"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { TopMenu } from "../components/TopMenu.jsx"
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch.js"
import NotFound from "./NotFound.jsx"
import s from "./Analytics.module.css"

const Analytics = () => {
	const authenticatedFetch = useAuthenticatedFetch()
	const { t, i18n } = useTranslation()

	const [report, setReport] = useState(null)
	const [status, setStatus] = useState("loading")

	const load = async () => {
		setStatus("loading")
		const response = await authenticatedFetch(
			"/api/admin/analytics/active-shops",
		)

		if (response?.status === 403) return setStatus("forbidden")
		if (!response?.ok) return setStatus("error")

		setReport(await response.json())
		setStatus("ready")
	}

	useEffect(() => {
		load().catch(() => setStatus("error"))
	}, [])

	// Only the server knows who may see this; to anyone else it doesn't exist.
	if (status === "forbidden") return <NotFound />

	const number = (value) => value.toLocaleString(i18n.language)
	const date = (value) =>
		new Date(value).toLocaleDateString(i18n.language, {
			day: "numeric",
			month: "short",
		})

	return (
		<>
			<TopMenu goBack="/" />

			<div className={s.page}>
				<div className={s.content}>
					<h1 className={s.title}>{t("analytics.title")}</h1>

					{status === "loading" && (
						<div className={s.center}>
							<Spinner />
						</div>
					)}

					{status === "error" && (
						<div className={s.center}>
							{t("analytics.error")}
							<Button
								onClick={() =>
									load().catch(() => setStatus("error"))
								}
							>
								{t("analytics.retry")}
							</Button>
						</div>
					)}

					{status === "ready" && (
						<>
							<p className={s.subtitle}>
								{t("analytics.subtitle", {
									minDays: report.regularUseMinDays,
									windowDays: report.windowDays,
								})}
							</p>

							<div className={s.tiles}>
								<Tile
									value={number(report.activeShops.length)}
									label={t("analytics.active_shops")}
								/>
								<Tile
									value={number(report.subscribedShops)}
									label={t("analytics.subscribed_shops")}
								/>
								<Tile
									value={number(
										report.activeShops.reduce(
											(sum, shop) =>
												sum + shop.productsImported,
											0,
										),
									)}
									label={t("analytics.products_imported", {
										windowDays: report.windowDays,
									})}
								/>
							</div>

							{report.activeShops.length === 0 ? (
								<div className={s.center}>
									{t("analytics.empty")}
								</div>
							) : (
								<div className={s.tableWrap}>
									<table className={s.table}>
										<thead>
											<tr>
												<th className={s.number}>#</th>
												<th>{t("analytics.shop")}</th>
												<th>{t("analytics.plan")}</th>
												<th>{t("analytics.status")}</th>
												<th className={s.number}>
													{t("analytics.products")}
												</th>
												<th className={s.number}>
													{t("analytics.active_days")}
												</th>
												<th>
													{t("analytics.last_import")}
												</th>
											</tr>
										</thead>
										<tbody>
											{report.activeShops.map(
												(shop, i) => (
													<tr key={shop.shop}>
														<td
															className={`${s.number} ${s.rank}`}
														>
															{i + 1}
														</td>
														<td>
															<a
																href={`https://${shop.shop}`}
																target="_blank"
																rel="noopener noreferrer"
															>
																{shop.shop}
															</a>
														</td>
														<td className={s.plans}>
															{shop.plans.join(
																", ",
															)}
														</td>
														<td>
															{t(
																`analytics.status_${shop.status}`,
															)}
														</td>
														<td
															className={s.number}
														>
															{number(
																shop.productsImported,
															)}
														</td>
														<td
															className={s.number}
														>
															{number(
																shop.activeDays,
															)}
														</td>
														<td>
															{date(
																shop.lastImportAt,
															)}
														</td>
													</tr>
												),
											)}
										</tbody>
									</table>
								</div>
							)}

							<div className={s.updated}>
								{t("analytics.updated", {
									time: new Date(
										report.generatedAt,
									).toLocaleTimeString(i18n.language, {
										hour: "2-digit",
										minute: "2-digit",
									}),
								})}
							</div>
						</>
					)}
				</div>
			</div>
		</>
	)
}

const Tile = ({ value, label }) => (
	<div className={s.tile}>
		<div className={s.tileValue}>{value}</div>
		<div className={s.tileLabel}>{label}</div>
	</div>
)

export default Analytics
